import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Loader2, Flag, QrCode, Crosshair, ChevronDown, ChevronUp, Printer, Unlock } from 'lucide-react';
import { LetterTemplateDialog } from './LetterTemplateDialog';
import { BreakupLetterRow, fmtDay } from './BreakupLetterRow';

export function BreakupReviewCard({ review, onPreview, onChanged, onQueueChanged }) {
  const [busyId, setBusyId] = useState('');
  const [edits, setEdits] = useState({});
  const [qrBusy, setQrBusy] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [onlyKpiDue, setOnlyKpiDue] = useState(false);
  const [kpiBusy, setKpiBusy] = useState('');
  const [relBusy, setRelBusy] = useState('');
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  useEffect(() => {
    api.printSettings().then(r => { setDevices(r.data?.devices || []); setDeviceId(r.data?.active_device_id || ''); }).catch(() => {});
  }, []);
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem('collapse:approvals.breakup') === '1'; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed(v => {
    try { window.localStorage.setItem('collapse:approvals.breakup', v ? '0' : '1'); } catch { /* ignore */ }
    return !v;
  });
  const allLetters = review?.letters || [];
  const dueLetters = allLetters.filter(l => l.due);
  const upcoming = allLetters.filter(l => !l.due);
  const kpiDue = allLetters.filter(l => l.kpi_due);
  const kpiIds = kpiDue.map(l => l.id);
  const qrReady = allLetters.filter(l => l.status === 'approved').length;
  if (!allLetters.length) return null;
  const collecting = !dueLetters.length;
  const baseLetters = collecting ? allLetters : dueLetters;
  const letters = onlyKpiDue && kpiDue.length ? baseLetters.filter(l => l.kpi_due) : baseLetters;
  const releaseAt = `${fmtDay(review.next_review_at)} 17:00`;
  const collectedSendBy = fmtDay(review.collected_send_by);

  const release = async (ids) => {
    setRelBusy(ids.length === 1 ? ids[0] : 'all');
    try {
      const r = await api.releaseBreakupLetters(ids);
      toast.success(`${r.data.released} Break-up${r.data.released !== 1 ? 's' : ''} freigegeben — jetzt unten in der Serien-Produktion · Versand bis ${fmtDay(r.data.send_by)}`, { duration: 9000 });
      onChanged?.();
      onQueueChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Freigabe fehlgeschlagen'); }
    finally { setRelBusy(''); }
  };

  const kpiAction = async (what) => {
    if (!kpiIds.length) return;
    setKpiBusy(what);
    try {
      if (what === 'qr') {
        const res = await api.downloadQrOverlaySelected(kpiIds, 'breakup');
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a'); a.href = url; a.download = `OLTA_Breakup_QR_Overlay_faellig_${kpiIds.length}x.pdf`; a.click();
        URL.revokeObjectURL(url);
        toast.success(`${res.headers?.['x-flyer-count'] || kpiIds.length} QR-Seiten für die fälligen Break-ups geladen (alphabetisch) — auf Break-up-Briefpapier drucken`, { duration: 8000 });
      } else if (what === 'letters') {
        const r = await api.createPrintBatch('pair', kpiIds, deviceId || null);
        toast.success(`Brief-Serie mit ${r.data.count} fälligen Break-ups vorbereitet${r.data.device_name ? ` · ${r.data.device_name}` : ''} — unten in der Serien-Produktion „Jetzt schreiben“`, { duration: 9000 });
        onQueueChanged?.();
      } else if (what === 'envelopes') {
        const r = await api.createPrintBatch('envelope', kpiIds, deviceId || null, 'breakup');
        toast.success(`Umschlag-Serie mit ${r.data.count} fälligen Break-ups vorbereitet${r.data.device_name ? ` · ${r.data.device_name}` : ''} — Break-up-Umschläge einlegen, dann „Jetzt schreiben“`, { duration: 9000 });
        onQueueChanged?.();
      }
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) { try { detail = JSON.parse(await err.response.data.text())?.detail; } catch { detail = ''; } }
      toast.error(detail || 'Aktion fehlgeschlagen', { duration: 9000 });
    } finally { setKpiBusy(''); }
  };

  const kpiRow = kpiDue.length > 0 && (
    <div className="px-4 py-2.5 border-b border-border bg-red-500/5 flex items-center gap-2 flex-wrap" data-testid="breakup-kpi-due-row">
      <Flag className="w-3.5 h-3.5 text-red-400 shrink-0" strokeWidth={1.5} />
      <span className="text-[11px] text-foreground">
        <span className="font-medium text-red-300" data-testid="breakup-kpi-due-count">{kpiDue.length} fällig diese Woche</span>
        <span className="text-muted-foreground"> — gesammelt bis {fmtDay(review.kpi_cutoff)} 17:00, Versand bis {fmtDay(review.kpi_deadline)} (KPI „Break-up-Briefe“).
          {allLetters.length - kpiDue.length > 0 && <> Die übrigen {allLetters.length - kpiDue.length} tragen ihre eigene „Versand bis“-Marke.</>}</span>
      </span>
      <span className="ml-auto flex items-center gap-2 flex-wrap">
        {devices.length > 1 && (
          <label className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="breakup-device-picker">
            <Printer className="w-3 h-3" /> Schreiben auf
            <select value={deviceId} onChange={e => setDeviceId(e.target.value)} data-testid="breakup-device-select"
              className="h-7 text-[10px] rounded-sm bg-secondary/40 border border-border px-1.5 text-foreground focus:outline-none">
              {devices.map(d => <option key={d.id} value={d.id}>{d.name}{d.online ? ' • online' : ' • offline'}{d.queued ? ` (${d.queued})` : ''}</option>)}
            </select>
          </label>
        )}
        <button onClick={() => setOnlyKpiDue(v => !v)} data-testid="breakup-kpi-filter-toggle"
          className={`text-[10px] px-2 py-1 rounded-sm border transition-colors ${onlyKpiDue ? 'border-red-500/50 bg-red-500/15 text-red-200' : 'border-border text-muted-foreground hover:text-foreground'}`}>
          {onlyKpiDue ? 'Alle zeigen' : `Nur fällige zeigen (${kpiDue.length})`}
        </button>
        <Button size="sm" variant="outline" onClick={() => kpiAction('qr')} disabled={kpiBusy !== ''} data-testid="breakup-kpi-qr-btn"
          className="h-7 text-[11px] rounded-sm border-sky-500/40 text-sky-300 hover:bg-sky-500/10">
          {kpiBusy === 'qr' ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />} QR-Overlay ({kpiDue.length})
        </Button>
        <Button size="sm" onClick={() => kpiAction('letters')} disabled={kpiBusy !== ''} data-testid="breakup-kpi-letters-btn"
          className="h-7 text-[11px] rounded-sm bg-red-600 hover:bg-red-500 text-white">
          {kpiBusy === 'letters' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />} Brief-Serie ({kpiDue.length}) vorbereiten
        </Button>
        <Button size="sm" variant="outline" onClick={() => kpiAction('envelopes')} disabled={kpiBusy !== ''} data-testid="breakup-kpi-envelopes-btn"
          className="h-7 text-[11px] rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
          {kpiBusy === 'envelopes' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />} Umschlag-Serie ({kpiDue.length})
        </Button>
      </span>
    </div>
  );

  const releaseAllBtn = (ids, testId) => (
    <Button size="sm" onClick={(e) => { e.stopPropagation(); release(ids); }} disabled={relBusy !== ''} data-testid={testId}
      title="Nicht bis Freitag warten: alle gesammelten Break-ups sofort freigeben → Serien-Produktion"
      className="h-7 text-[11px] rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">
      {relBusy === 'all' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />} Alle {ids.length} jetzt freigeben
    </Button>
  );

  // Not-yet-due letters: dates Talu asked for — when they auto-release and by when the batch must be posted.
  const collectedText = (n) => (
    <span className="text-[11px] text-muted-foreground">
      <span className="text-foreground font-medium">{n} Break-up-Brief{n !== 1 ? 'e' : ''} gesammelt</span>
      {' '}(seit {fmtDay(review.cutoff)} 17:00) — Auto-Freigabe <span className="text-amber-300">{releaseAt}</span>,
      {' '}<span className="text-foreground">Versand bis {collectedSendBy}</span>. Oder jetzt freigeben → sofort schreibbar.
    </span>
  );

  const banner = collecting && (
    <div role="button" tabIndex={0} onClick={() => setExpanded(v => !v)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); } }}
      className="w-full text-left bg-card border border-border rounded-sm px-4 py-2.5 flex items-center gap-2 flex-wrap hover:border-amber-500/40 transition-colors cursor-pointer"
      data-testid="breakup-collect-banner">
      <Flag className="w-3.5 h-3.5 text-amber-400 shrink-0" strokeWidth={1.5} />
      {collectedText(allLetters.length)}
      <span className="ml-auto flex items-center gap-2 shrink-0">
        {releaseAllBtn(allLetters.map(l => l.id), 'breakup-release-all-btn')}
        <span className="flex items-center gap-1 text-[11px] text-amber-300" data-testid="breakup-collect-toggle">
          {expanded ? <>Einklappen <ChevronUp className="w-3.5 h-3.5" /></> : <>Vorab ansehen <ChevronDown className="w-3.5 h-3.5" /></>}
        </span>
      </span>
    </div>
  );

  if (collecting && !expanded) return banner;

  const run = async (l, payload, okMsg) => {
    setBusyId(l.id);
    try {
      await api.updateLetter(l.id, payload);
      toast.success(okMsg);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusyId(''); }
  };

  const saveBody = (l) => run(l, { body: edits[l.id] }, `${l.company_name}: Text gespeichert`)
    .then(() => setEdits(e => { const n = { ...e }; delete n[l.id]; return n; }));

  const downloadQr = async (kind) => {
    setQrBusy(kind);
    try {
      const res = kind === 'overlay' ? await api.downloadBreakupQrOverlayPdf() : await api.downloadBreakupQrTestPdf();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = kind === 'overlay'
        ? `OLTA_Breakup_QR_Overlay_${new Date().toISOString().slice(0, 10)}.pdf`
        : 'OLTA_Breakup_QR_Kalibrierung.pdf';
      a.click();
      URL.revokeObjectURL(url);
      if (kind === 'overlay') {
        const n = res.headers['x-flyer-count'];
        toast.success(`QR-Overlay mit ${n || qrReady} Seite${(n || qrReady) !== '1' ? 'n' : ''} geladen — auf die Briefpapier-Vordrucke drucken`);
      }
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text())?.detail; } catch { detail = null; }
      }
      toast.error(detail || 'Download fehlgeschlagen');
    } finally { setQrBusy(''); }
  };

  const qrButton = (
    <Button size="sm" onClick={() => downloadQr('overlay')} disabled={qrBusy !== '' || qrReady === 0}
      data-testid="breakup-qr-overlay-btn"
      title={qrReady === 0 ? 'Erst Briefe freigeben' : ''}
      className="h-7 text-[11px] rounded-sm bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-40">
      {qrBusy === 'overlay' ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
      QR-Overlay laden ({qrReady})
    </Button>
  );

  const rowProps = { review, edits, setEdits, busyId, relBusy, onPreview, onSave: saveBody, onRelease: release };

  return (
    <div className="space-y-2">
    {banner}
    <div className="bg-card border border-amber-500/40 rounded-sm" data-testid="breakup-review-card">
      <div role="button" tabIndex={0} onClick={toggleCollapsed} aria-expanded={!collapsed}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapsed(); } }}
        data-testid="breakup-review-toggle"
        className={`px-4 py-3 flex items-center gap-2 flex-wrap cursor-pointer select-none hover:bg-secondary/30 transition-colors ${collapsed ? '' : 'border-b border-border'}`}>
        <Flag className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
        <span className="text-sm font-medium text-foreground">Break-up-Briefe der Woche</span>
        <span className="text-[10px] font-mono text-amber-300 border border-amber-500/40 rounded-sm px-1.5 py-0.5"
          data-testid="breakup-review-count">{letters.length}</span>
        {collapsed && (
          <span className="text-[11px] text-muted-foreground" data-testid="breakup-review-collapsed-summary">
            {qrReady} freigegeben · bereit für QR &amp; iAuto
            {kpiDue.length > 0 && <> · <span className="text-red-300 font-medium">{kpiDue.length} fällig bis {fmtDay(review.kpi_deadline)}</span></>}
            {upcoming.length > 0 && <> · {upcoming.length} gesammelt (Versand bis {collectedSendBy})</>}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2" onClick={e => e.stopPropagation()}>
          {collapsed && qrButton}
          <LetterTemplateDialog canEdit />
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground/60 shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
      </div>
      {!collapsed && (<>
      {kpiRow}
      <p className="px-4 py-2 text-[11px] text-muted-foreground border-b border-border">
        Letzter Touch der Sequenzen — unter der Woche gesammelt, freitags ab 17:00 automatisch freigegeben
        (oder jederzeit per „Jetzt freigeben“). Freigegebene Briefe (Standard-Text, kein Finalisieren nötig):
        Text nachjustieren falls nötig, Vorschau prüfen, QR-Overlay drucken — Versand über die iAuto-Queue
        bis zur „Versand bis“-Marke am Brief.
      </p>
      <div className="px-4 py-2.5 border-b border-border bg-sky-500/5 flex items-center gap-2 flex-wrap"
        data-testid="breakup-qr-overlay-row">
        <QrCode className="w-3.5 h-3.5 text-sky-400 shrink-0" />
        <span className="text-[11px] text-muted-foreground">
          Vor dem iAuto-Schreiben: QR-Codes auf das Break-up-Briefpapier drucken (Feld unten rechts) —
          eigener QR pro Unternehmer, Scans zählen klar als <span className="text-sky-300">Break-up-Brief</span>.
          Reihenfolge alphabetisch wie die Schreib-Serie.
        </span>
        <span className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => downloadQr('test')} disabled={qrBusy !== ''}
            data-testid="breakup-qr-test-btn"
            className="h-7 text-[11px] rounded-sm border-border text-muted-foreground hover:text-foreground">
            {qrBusy === 'test' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crosshair className="w-3 h-3" />} Testseite
          </Button>
          {qrButton}
        </span>
      </div>
      <div className="divide-y divide-border">
        {letters.map(l => <BreakupLetterRow key={l.id} l={l} {...rowProps} />)}
      </div>
      {!collecting && upcoming.length > 0 && (
        <div className="border-t border-amber-500/30" data-testid="breakup-upcoming-block">
          <div role="button" tabIndex={0} onClick={() => setUpcomingOpen(v => !v)} data-testid="breakup-upcoming-toggle"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setUpcomingOpen(v => !v); } }}
            className="w-full text-left px-4 py-2.5 bg-amber-500/5 flex items-center gap-2 flex-wrap hover:bg-amber-500/10 transition-colors cursor-pointer">
            <Flag className="w-3.5 h-3.5 text-amber-400 shrink-0" strokeWidth={1.5} />
            {collectedText(upcoming.length)}
            <span className="ml-auto flex items-center gap-2 shrink-0">
              {releaseAllBtn(upcoming.map(l => l.id), 'breakup-upcoming-release-all-btn')}
              <span className="flex items-center gap-1 text-[11px] text-amber-300">
                {upcomingOpen ? <>Einklappen <ChevronUp className="w-3.5 h-3.5" /></> : <>Ansehen <ChevronDown className="w-3.5 h-3.5" /></>}
              </span>
            </span>
          </div>
          {upcomingOpen && (
            <div className="divide-y divide-border border-t border-border" data-testid="breakup-upcoming-list">
              {upcoming.map(l => <BreakupLetterRow key={l.id} l={l} {...rowProps} />)}
            </div>
          )}
        </div>
      )}
      </>)}
    </div>
    </div>
  );
}
